import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusChip, cad, day, errMsg } from "@/components/sponsored/format";

export function FeeReviewButton({ conv, reasons, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/merchant/performance-fee-reviews", { verified_conversion_id: conv.id, reason, details });
      toast.success("Review requested. Cyannect will decide and let you know.");
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't send the review request"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="text-xs font-semibold text-[#004C3F] underline-offset-2 hover:underline" data-testid={`fee-review-open-${conv.id}`}>Request review</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="fee-review-dialog">
          <DialogHeader>
            <DialogTitle>Ask Cyannect to review this fee</DialogTitle>
            <DialogDescription>Order {conv.order_id} · fee {cad(conv.applied_fee)}. Ordinary refunds or returns don't qualify; only Cyannect can approve a correction.</DialogDescription>
          </DialogHeader>
          <select className="pf-input w-full" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="fee-review-reason">
            <option value="">Choose a reason</option>
            {reasons.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
          <textarea className="pf-input w-full min-h-[96px]" maxLength={2000} placeholder="What happened? Include anything that helps us check." value={details}
            onChange={(e) => setDetails(e.target.value)} data-testid="fee-review-details" />
          <DialogFooter>
            <button onClick={send} disabled={!reason || busy} className="pf-btn-primary px-5 py-2 rounded-full text-sm disabled:opacity-50" data-testid="fee-review-submit">{busy ? "Sending…" : "Send for review"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function FeeReviewsCard({ rows, reasons }) {
  if (!rows.length) return null;
  const label = Object.fromEntries(reasons.map((r) => [r.id, r.label]));
  return (
    <div className="pf-card p-5 mt-6" data-testid="fee-reviews-card">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Your Performance Fee review requests</div>
      <ul className="divide-y divide-slate-100 text-sm">
        {rows.map((r) => (
          <li key={r.id} className="py-2 flex flex-wrap items-center justify-between gap-2" data-testid={`fee-review-row-${r.id}`}>
            <span className="text-slate-700"><span className="font-mono text-xs">{r.order_id}</span> · {label[r.reason] || r.reason} · {cad(r.fee_cad)} · {day(r.requested_at)}</span>
            <span className="flex items-center gap-2">{r.decision_note && <span className="text-xs text-slate-500">{r.decision_note}</span>}<StatusChip status={r.status} /></span>
          </li>
        ))}
      </ul>
    </div>
  );
}
